import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ZoomIn, X, Download } from 'lucide-react';
import { CommunityAmenity, getAllCategories, getAmenitiesByCategory } from '../data/communityAmenities';

interface CommunityAmenitiesProps {
  featuredAmenityIds?: string[];
  showAll?: boolean;
  className?: string;
}

export const CommunityAmenities = ({ 
  featuredAmenityIds = [], 
  showAll = true,
  className = '' 
}: CommunityAmenitiesProps) => {
  const [selectedImage, setSelectedImage] = useState<{ amenity: CommunityAmenity; imageIndex: number } | null>(null);
  // Order categories by hierarchy: The Pearl (most granular) -> Grand Harbour -> Cayman Islands (broadest)
  const categoryOrder: CommunityAmenity["category"][] = ["The Pearl", "Grand Harbour", "Cayman Islands"];
  const allCategories = getAllCategories();
  const categories = categoryOrder.filter(cat => allCategories.includes(cat));

  const openLightbox = (amenity: CommunityAmenity, imageIndex: number) => {
    if (amenity.images.length > 0) {
      setSelectedImage({ amenity, imageIndex });
    }
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    if (!selectedImage) return;
    const { amenity, imageIndex } = selectedImage;
    const nextIndex = (imageIndex + 1) % amenity.images.length;
    setSelectedImage({ amenity, imageIndex: nextIndex });
  };

  const prevImage = () => {
    if (!selectedImage) return;
    const { amenity, imageIndex } = selectedImage;
    const prevIndex = (imageIndex - 1 + amenity.images.length) % amenity.images.length;
    setSelectedImage({ amenity, imageIndex: prevIndex });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  };

  const renderAmenityCard = (amenity: CommunityAmenity) => {
    const isFeatured = featuredAmenityIds.includes(amenity.id);
    
    return (
      <Card 
        key={amenity.id} 
        className={`group relative overflow-hidden transition-all duration-300 hover:shadow-luxury ${
          isFeatured ? 'ring-2 ring-brand-teal/30' : ''
        }`}
      >
        <CardContent className="p-0">
          {/* Image Section */}
          <div className="relative aspect-[4/3] overflow-hidden">
            {amenity.images.length > 0 ? (
              <img
                src={amenity.images[0]}
                alt={amenity.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-soft-cream to-brand-teal/20 flex items-center justify-center">
                <span className="text-6xl opacity-50">{amenity.icon}</span>
              </div>
            )}
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Featured Badge */}
            {isFeatured && (
              <div className="absolute top-3 left-3">
                <Badge className="bg-brand-teal/90 text-white border-0">
                  Featured
                </Badge>
              </div>
            )}
            
            {/* Zoom Icon - only show if images exist */}
            {amenity.images.length > 0 && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button
                  onClick={() => openLightbox(amenity, 0)}
                  className="p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200"
                  aria-label="View images"
                >
                  <ZoomIn className="w-6 h-6 text-white" />
                </button>
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0">
                {amenity.label && (
                  <p className="text-xs font-medium tracking-wide uppercase text-brand-teal/90 mb-1">
                    {amenity.label}
                  </p>
                )}
                <h3 className="font-semibold text-lg text-foreground">
                  {amenity.headline ?? amenity.name}
                </h3>
              </div>
              {amenity.icon && (
                <span className="text-2xl ml-2">{amenity.icon}</span>
              )}
            </div>
            
            <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
              {amenity.description}
            </p>
            
            {/* Highlights */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-foreground">Key Features:</h4>
              <div className="flex flex-wrap gap-2">
                {amenity.highlights.slice(0, 3).map((highlight, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {highlight}
                  </Badge>
                ))}
                {amenity.highlights.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{amenity.highlights.length - 3} more
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const getCategoryDescription = (category: CommunityAmenity["category"]): string => {
    switch (category) {
      case "The Pearl":
        return "We believe a home is more than its walls—it’s the life it invites. From the quiet expanse of the lagoon to the vibrant pulse of the plaza, each amenity weaves into the rhythm of your life at The Pearl.";
      case "Grand Harbour":
        return "Discover the neighborhood benefits and nearby attractions that make Grand Harbour an exceptional location.";
      case "Cayman Islands":
        return "Explore the broader advantages of living in the Cayman Islands, from tax benefits to world-class lifestyle.";
      default:
        return "";
    }
  };

  return (
    <div className={`space-y-8 ${className}`}>
      <Tabs defaultValue="The Pearl" className="w-full">
        <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 h-auto sm:h-10 mb-8">
          {categories.map((category) => (
            <TabsTrigger key={category} value={category} className="text-sm py-2 sm:py-1.5">
              {category}
            </TabsTrigger>
          ))}
        </TabsList>

        {categories.map((category) => {
          const amenities = getAmenitiesByCategory(category);
          const filteredAmenities = showAll 
            ? amenities 
            : amenities.filter(amenity => featuredAmenityIds.includes(amenity.id));

          if (filteredAmenities.length === 0) return null;

          return (
            <TabsContent key={category} value={category} className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="font-display text-2xl lg:text-3xl font-semibold text-foreground mb-2">
                  {category}
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  {getCategoryDescription(category)}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAmenities.map(renderAmenityCard)}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <div className="relative max-w-7xl max-h-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white"
            >
              <X className="w-5 h-5" />
            </Button>

            {/* Download Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const link = document.createElement('a');
                link.href = selectedImage.amenity.images[selectedImage.imageIndex];
                link.download = `${selectedImage.amenity.name}-${selectedImage.imageIndex + 1}.jpg`;
                link.click();
              }}
              className="absolute top-4 right-16 z-10 bg-black/50 hover:bg-black/70 text-white"
            >
              <Download className="w-5 h-5" />
            </Button>

            {/* Main Image */}
            <img
              src={selectedImage.amenity.images[selectedImage.imageIndex]}
              alt={`${selectedImage.amenity.name} - Image ${selectedImage.imageIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-xl"
            />

            {/* Navigation */}
            {selectedImage.amenity.images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </>
            )}

            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {selectedImage.imageIndex + 1} of {selectedImage.amenity.images.length}
            </div>

            {/* Thumbnail Strip */}
            {selectedImage.amenity.images.length > 1 && (
              <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2">
                {selectedImage.amenity.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage({ amenity: selectedImage.amenity, imageIndex: index });
                    }}
                    className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                      index === selectedImage.imageIndex
                        ? 'border-white'
                        : 'border-transparent hover:border-white/50'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

